import { sumBy } from '../number'

export type ValueHistogram = {
  minimum: number
  maximum: number
  counts: number[]
  below: number
  above: number
}

export type ValueZone = {
  min: number
  max: number
  share: number
}

export enum BreakMethod {
  EqualCount = 'equalCount',
  EqualInterval = 'equalInterval',
  NaturalBreaks = 'naturalBreaks'
}

export enum ZoneBreaksFailure {
  NoData = 'noData',
  SingleValue = 'singleValue'
}

export type ZoneBreaks = { ok: true; zones: ValueZone[] } | { ok: false; reason: ZoneBreaksFailure }

const runningTotals = ({ start, counts }: { start: number; counts: number[] }) => {
  const totals = [start]
  for (const count of counts) {
    totals.push(totals[totals.length - 1] + count)
  }
  return totals
}

const indexOfClosest = ({ values, target }: { values: number[]; target: number }) =>
  values.reduce(
    (best, value, index) =>
      Math.abs(value - target) < Math.abs(values[best] - target) ? index : best,
    0
  )

const equalCountEdges = ({
  countBelowEdge,
  zoneCount
}: {
  countBelowEdge: number[]
  zoneCount: number
}) => {
  const total = countBelowEdge[countBelowEdge.length - 1]
  return Array.from({ length: zoneCount - 1 }, (_, i) =>
    indexOfClosest({ values: countBelowEdge, target: ((i + 1) * total) / zoneCount })
  )
}

const equalIntervalEdges = ({ binCount, zoneCount }: { binCount: number; zoneCount: number }) =>
  Array.from({ length: zoneCount - 1 }, (_, i) => Math.round(((i + 1) * binCount) / zoneCount))

const naturalBreakEdges = ({
  histogram,
  zoneCount
}: {
  histogram: ValueHistogram
  zoneCount: number
}) => {
  const { minimum, maximum, counts, below, above } = histogram
  const binCount = counts.length
  const binWidth = (maximum - minimum) / binCount
  const weights = counts.map((count, i) => {
    const outside = (i === 0 ? below : 0) + (i === binCount - 1 ? above : 0)
    return count + outside
  })

  const weight = runningTotals({ start: 0, counts: weights })
  const weighted = runningTotals({
    start: 0,
    counts: weights.map((w, i) => w * (minimum + (i + 0.5) * binWidth))
  })
  const squared = runningTotals({
    start: 0,
    counts: weights.map((w, i) => w * (minimum + (i + 0.5) * binWidth) ** 2)
  })

  const cost = (from: number, to: number) => {
    const w = weight[to] - weight[from]
    if (w === 0) return 0
    const s = weighted[to] - weighted[from]
    return squared[to] - squared[from] - (s * s) / w
  }

  let best = Array.from({ length: binCount + 1 }, (_, to) => cost(0, to))
  const splits: number[][] = []

  for (let group = 1; group < zoneCount; group++) {
    const next = Array.from({ length: binCount + 1 }, () => Infinity)
    const from = Array.from({ length: binCount + 1 }, () => 0)

    for (let to = group + 1; to <= binCount; to++) {
      for (let split = group; split < to; split++) {
        const candidate = best[split] + cost(split, to)
        if (candidate < next[to]) {
          next[to] = candidate
          from[to] = split
        }
      }
    }

    splits.push(from)
    best = next
  }

  const edges: number[] = []
  let to = binCount
  for (let group = zoneCount - 2; group >= 0; group--) {
    to = splits[group][to]
    edges.unshift(to)
  }

  return edges
}

const zonesFromEdges = ({
  histogram,
  edges
}: {
  histogram: ValueHistogram
  edges: number[]
}): ZoneBreaks => {
  const { minimum, maximum, counts, below } = histogram
  const countBelowEdge = runningTotals({ start: below, counts })
  const total = countBelowEdge[countBelowEdge.length - 1] + histogram.above

  if (total === 0) {
    return { ok: false, reason: ZoneBreaksFailure.NoData }
  }

  const binWidth = (maximum - minimum) / counts.length
  const breakEdges = edges
    .toSorted((a, b) => a - b)
    .filter((edge) => edge > 0 && edge < counts.length && countBelowEdge[edge] < total)
    .reduce<number[]>((kept, edge) => {
      const previous = kept.at(-1)
      const countBelowPrevious = previous === undefined ? 0 : countBelowEdge[previous]
      return countBelowEdge[edge] > countBelowPrevious ? [...kept, edge] : kept
    }, [])

  if (breakEdges.length === 0) {
    return { ok: false, reason: ZoneBreaksFailure.SingleValue }
  }

  const bounds = [
    { value: minimum, countBelow: 0 },
    ...breakEdges.map((edge) => ({
      value: minimum + edge * binWidth,
      countBelow: countBelowEdge[edge]
    })),
    { value: maximum, countBelow: total }
  ]

  return {
    ok: true,
    zones: bounds.slice(1).map((upper, i) => ({
      min: bounds[i].value,
      max: upper.value,
      share: (upper.countBelow - bounds[i].countBelow) / total
    }))
  }
}

export const zoneBreaks = ({
  histogram,
  zoneCount,
  method
}: {
  histogram: ValueHistogram
  zoneCount: number
  method: BreakMethod
}): ZoneBreaks => {
  const countBelowEdge = runningTotals({ start: histogram.below, counts: histogram.counts })
  countBelowEdge[countBelowEdge.length - 1] += histogram.above

  const edgesByMethod = {
    [BreakMethod.EqualCount]: () => equalCountEdges({ countBelowEdge, zoneCount }),
    [BreakMethod.EqualInterval]: () =>
      equalIntervalEdges({ binCount: histogram.counts.length, zoneCount }),
    [BreakMethod.NaturalBreaks]: () => naturalBreakEdges({ histogram, zoneCount })
  }

  return zonesFromEdges({ histogram, edges: edgesByMethod[method]() })
}

export const zonesForBreaks = ({
  histogram,
  breaks
}: {
  histogram: ValueHistogram
  breaks: number[]
}): ZoneBreaks => {
  const { minimum, maximum, counts } = histogram
  const binWidth = (maximum - minimum) / counts.length
  const edges = breaks.map((value) =>
    Math.min(counts.length, Math.max(0, Math.round((value - minimum) / binWidth)))
  )
  return zonesFromEdges({ histogram, edges })
}

export type WeightedValue = { value: number; weight: number }

export type WeightedHistogram =
  | { ok: true; histogram: ValueHistogram }
  | { ok: false; reason: ZoneBreaksFailure }

export const weightedHistogram = ({
  values,
  bins
}: {
  values: readonly WeightedValue[]
  bins: number
}): WeightedHistogram => {
  const weighted = values.filter(({ weight }) => weight > 0)
  if (weighted.length === 0) return { ok: false, reason: ZoneBreaksFailure.NoData }

  const minimum = Math.min(...weighted.map(({ value }) => value))
  const maximum = Math.max(...weighted.map(({ value }) => value))
  if (maximum <= minimum) return { ok: false, reason: ZoneBreaksFailure.SingleValue }

  const binWidth = (maximum - minimum) / bins
  const counts = Array.from({ length: bins }, () => 0)
  for (const { value, weight } of weighted) {
    const bin = Math.min(bins - 1, Math.floor((value - minimum) / binWidth))
    counts[bin] += weight
  }

  return { ok: true, histogram: { minimum, maximum, counts, below: 0, above: 0 } }
}

export const zoneIndexOf = ({ value, breaks }: { value: number; breaks: readonly number[] }) =>
  breaks.filter((edge) => value >= edge).length

const spreadOf = (values: readonly WeightedValue[]) => {
  const weight = sumBy(values, (value) => value.weight)
  if (weight === 0) return 0
  const mean = sumBy(values, (value) => value.weight * value.value) / weight
  return sumBy(values, (value) => value.weight * (value.value - mean) ** 2)
}

export const varianceExplained = ({
  values,
  breaks
}: {
  values: readonly WeightedValue[]
  breaks: readonly number[]
}): number => {
  const total = spreadOf(values)
  if (total === 0) return 0

  const zones = Map.groupBy(values, ({ value }) => zoneIndexOf({ value, breaks }))
  return 1 - sumBy([...zones.values()], spreadOf) / total
}

export const breaksBetweenValues = ({
  values,
  breaks
}: {
  values: readonly WeightedValue[]
  breaks: readonly number[]
}): number[] => {
  const sorted = values.map(({ value }) => value).toSorted((a, b) => a - b)
  return breaks.map((edge) => {
    const above = sorted.findIndex((value) => value >= edge)
    if (above <= 0) return edge
    return (sorted[above - 1] + sorted[above]) / 2
  })
}
